// TrendingCard — CoinGecko Trending Coins der letzten 24h mit Spring-Stagger,
// Magnetic-Hover, Top-3-Hervorhebung.

import { motion, useReducedMotion } from 'framer-motion';
import { Flame, Sparkles, TrendingUp } from 'lucide-react';
import type { FullData } from '@/types/market';

export interface TrendingCardProps {
  fullData: FullData | null;
  limit?: number;
  /** Optional: pro Coin „Frag EVE"-Button mit Loading-State. */
  onAskEve?: (coin: { symbol: string; name: string }) => void;
  researchingSymbol?: string | null;
}

export function TrendingCard({ fullData, limit = 7, onAskEve, researchingSymbol }: TrendingCardProps) {
  const v3Flat = (fullData?.trending as unknown) as
    | Array<{ symbol?: string; name?: string; marketCapRank?: number | null }>
    | undefined;
  const coins = (() => {
    if (Array.isArray(v3Flat)) {
      return v3Flat.slice(0, limit).map((c) => ({
        symbol: c.symbol ?? '',
        name: c.name ?? '',
        rank: c.marketCapRank ?? null,
      }));
    }
    const v1 = fullData?.trending?.coins ?? [];
    return v1.slice(0, limit).map((c) => ({
      symbol: c.item?.symbol ?? '',
      name: c.item?.name ?? '',
      rank: c.item?.market_cap_rank ?? null,
    }));
  })();

  return (
    <section className="relative overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-br from-neutral-900/80 via-neutral-950/80 to-neutral-900/80 p-5 backdrop-blur-sm">
      <div className="pointer-events-none absolute -right-12 -bottom-12 h-32 w-32 rounded-full bg-rose-500/15 blur-3xl" />

      <header className="relative mb-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="grid size-8 place-items-center rounded-lg border border-rose-500/30 bg-rose-500/10">
            <Flame className="size-4 text-rose-300" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-neutral-100">Trending Coins</h3>
            <p className="text-[10px] uppercase tracking-wider text-neutral-500">
              Worauf Retail schaut · CoinGecko 24h
            </p>
          </div>
        </div>
        <span className="rounded-full border border-rose-400/30 bg-rose-400/10 px-2 py-0.5 text-[10px] font-mono text-rose-300">
          24H
        </span>
      </header>

      {coins.length === 0 ? (
        <div className="rounded-lg border border-white/5 bg-neutral-900/40 p-4 text-center text-xs text-neutral-500">
          Keine Trending-Daten geladen.
        </div>
      ) : (
        <ol className="relative space-y-1.5">
          {coins.map((c, i) => (
            <TrendingRow
              key={`${c.symbol}-${i}`}
              position={i + 1}
              symbol={c.symbol}
              name={c.name}
              mcapRank={c.rank}
              onAskEve={onAskEve}
              isResearching={researchingSymbol === c.symbol}
            />
          ))}
        </ol>
      )}
    </section>
  );
}

interface TrendingRowProps {
  position: number;
  symbol: string;
  name: string;
  mcapRank: number | null;
  onAskEve?: (coin: { symbol: string; name: string }) => void;
  isResearching?: boolean;
}

function TrendingRow({ position, symbol, name, mcapRank, onAskEve, isResearching }: TrendingRowProps) {
  const reduceMotion = useReducedMotion();
  const isTop3 = position <= 3;

  return (
    <motion.li
      whileHover={reduceMotion ? undefined : { x: 4 }}
      transition={{ type: 'spring', stiffness: 400, damping: 28 }}
      className={`group relative flex items-center gap-3 overflow-hidden rounded-lg px-3 py-2 transition-colors ${
        isTop3
          ? 'bg-gradient-to-r from-amber-500/10 via-amber-500/5 to-transparent hover:from-amber-500/15'
          : 'hover:bg-white/[0.03]'
      }`}
    >
      <div
        className={`grid size-7 shrink-0 place-items-center rounded-md text-[11px] font-bold tabular ${
          isTop3
            ? 'border border-amber-500/40 bg-amber-500/15 text-amber-300'
            : 'border border-white/5 bg-neutral-800/60 text-neutral-500'
        }`}
      >
        {position}
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="font-mono text-[13px] font-semibold text-neutral-100">{symbol}</span>
          {isTop3 ? (
            <Flame className="size-3 text-amber-400 transition-transform group-hover:scale-125" />
          ) : null}
        </div>
        <div className="truncate text-[11px] text-neutral-500">{name}</div>
      </div>

      <div className="shrink-0 text-right">
        <div className="text-[10px] uppercase text-neutral-600">Mcap</div>
        <div className="text-[11px] tabular text-neutral-500">
          {mcapRank != null ? `#${mcapRank}` : '—'}
        </div>
      </div>

      {onAskEve ? (
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (!isResearching) onAskEve({ symbol, name });
          }}
          disabled={isResearching}
          className="group/btn flex shrink-0 items-center gap-1 rounded-md border border-amber-500/30 bg-amber-500/10 px-2 py-1 text-[10px] font-medium text-amber-200 transition-all hover:border-amber-400/60 hover:bg-amber-500/20 disabled:opacity-60 disabled:cursor-wait"
          title={isResearching ? 'Recherche läuft…' : 'Frag EVE warum trended'}
        >
          <Sparkles className={`size-3 ${isResearching ? 'animate-pulse' : ''}`} />
          {isResearching ? '…' : 'EVE'}
        </button>
      ) : (
        <TrendingUp
          className={`size-3.5 shrink-0 transition-all ${
            isTop3 ? 'text-amber-400' : 'text-neutral-600'
          } group-hover:translate-x-1`}
        />
      )}
    </motion.li>
  );
}
